import { getLogger } from "./logging";

const log = getLogger("🛠️ Utils");

export function runAfterNextRepaint(callback, ...args) {
  if (args.length)
    return requestAnimationFrame(() =>
      requestAnimationFrame(() => callback(...args))
    );
  else return requestAnimationFrame(() => requestAnimationFrame(callback));
}

export function prettyDateFormat(date) {
  const now = new Date();
  const diffInSeconds = Math.round((now - date) / 1000);
  const diffInMinutes = Math.round(diffInSeconds / 60);
  const diffInHours = Math.round(diffInMinutes / 60);
  const diffInDays = Math.round(diffInHours / 24);

  if (diffInSeconds < 60) return "just now";
  else if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
  else if (diffInHours < 24) return `${diffInHours} hours ago`;
  else if (diffInDays === 1) return "Yesterday";
  else if (diffInDays < 7) {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  }
  // Format as 'Month Day, Year' for older dates
  else
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
}

export function prettyTimeDeltaFormat(ms) {
  const seconds = Math.round(ms / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 60) {
    return seconds > 1 ? `${seconds} seconds` : `${seconds} second`;
  } else if (minutes < 60) {
    return minutes > 1 ? `${minutes} minutes` : `${minutes} minute`;
  } else if (hours < 24) {
    return hours > 1 ? `${hours} hours` : `${hours} hour`;
  } else {
    return days > 1 ? `${days} days` : `${days} day`;
  }
}

export function addScrollContainerFocusEventCallback(
  el,
  scrollContainer,
  disableManualScroll = true,
  focusEvent = "focus"
) {
  el.addEventListener(focusEvent, (event) => {
    const elementRect = event.target.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + scrollContainer.scrollTop;
    const scrollPosition = absoluteElementTop - window.innerHeight * 0.035;
    scrollContainer.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  });
  if (!disableManualScroll) return;
  el.addEventListener("blur", () => {
    scrollContainer.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  });
}

export function formatNumber(num) {
  if (num < 1000) return num; // return the same number if less than 1000
  if (num < 1000000) return (num / 1000).toFixed(1) + "K"; // convert to K for thousands
  if (num < 1000000000) return (num / 1000000).toFixed(1) + "M"; // convert to M for millions
  return (num / 1000000000).toFixed(1) + "B"; // convert to B for billions
}

export class JobExecutor {
  constructor() {
    this.jobQueue = [];
    this.isJobRunning = false;
  }

  // Function to add a job to the queue
  addJob(jobFunction, ...args) {
    // Ensure the jobFunction is a function returning a promise
    if (typeof jobFunction !== "function") {
      throw new Error("Job must be a function that returns a promise.");
    }

    // Add the job to the queue
    this.jobQueue.push([jobFunction, args]);

    // If no job is currently running, start the execution
    if (!this.isJobRunning) {
      this.runNextJob();
    }
  }

  // Function to run the next job in the queue
  async runNextJob() {
    if (this.jobQueue.length === 0) {
      this.isJobRunning = false;
      return;
    }

    this.isJobRunning = true;
    const [jobFunction, args] = this.jobQueue.shift();

    try {
      await jobFunction(...args);
    } catch (err) {
      log.error("Error in job execution", err);
    } finally {
      await this.runNextJob();
    }
  }
}

export function randomChoice(arr) {
  return arr[Math.floor(arr.length * Math.random())];
}

export const orderObjectByKeys = (obj) =>
  Object.keys(obj)
    .sort()
    .reduce((o, key) => {
      o[key] = obj[key];
      return o;
    }, {});
